import React, { useState, useEffect } from 'react';
import { Printer, X, FileText, Loader } from 'lucide-react';
import Modal from '../../ui/Modal';
import electronService from '../../../services/electron/electronService';
import { formatMoney } from '../../../utils/formatters';
import './PrintOrderModal.css';

/**
 * Modal para imprimir pedido - soporta ticket (80mm) y formato (carta)
 */
const PrintOrderModal = ({
    isOpen,
    onClose,
    onPrintComplete,
    ventaData,
    empresaLogo,
    showToast,
    tipoImpresionDefault = 'formato',
    impresoraPedidos = ''
}) => {
    const [printers, setPrinters] = useState([]);
    const [selectedPrinter, setSelectedPrinter] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingPrinters, setLoadingPrinters] = useState(true);
    const [tipoImpresion, setTipoImpresion] = useState(tipoImpresionDefault);

    // Sincronizar tipo de impresión con la config cuando cambia
    useEffect(() => {
        setTipoImpresion(tipoImpresionDefault);
    }, [tipoImpresionDefault]);

    // Cargar impresoras al abrir
    useEffect(() => {
        if (isOpen) {
            loadPrinters();
        }
    }, [isOpen]);

    const loadPrinters = async () => {
        setLoadingPrinters(true);
        try {
            const lastUsedPrinter = localStorage.getItem('pos_ultima_impresora');

            if (electronService.isElectronApp()) {
                const result = await electronService.getPrinters();
                if (result && Array.isArray(result)) {
                    setPrinters(result);
                    // Prioridad: configurada para pedidos > última usada > por defecto > primera
                    const configPrinter = impresoraPedidos ? result.find(p => p.name === impresoraPedidos) : null;
                    const lastPrinter = result.find(p => p.name === lastUsedPrinter);
                    if (configPrinter) {
                        setSelectedPrinter(configPrinter.name);
                    } else if (lastPrinter) {
                        setSelectedPrinter(lastPrinter.name);
                    } else {
                        const defaultPrinter = result.find(p => p.isDefault);
                        if (defaultPrinter) {
                            setSelectedPrinter(defaultPrinter.name);
                        } else if (result.length > 0) {
                            setSelectedPrinter(result[0].name);
                        }
                    }
                }
            } else {
                setPrinters([{ name: 'system', displayName: 'Impresora del sistema' }]);
                setSelectedPrinter('system');
            }
        } catch (error) {
            console.error('Error cargando impresoras:', error);
            showToast && showToast('Error al cargar impresoras', 'error');
        } finally {
            setLoadingPrinters(false);
        }
    };

    // Ajustar texto a ancho máximo de caracteres
    const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        words.forEach(word => {
            if ((currentLine + ' ' + word).trim().length > maxWidth) {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = currentLine ? currentLine + ' ' + word : word;
            }
        });
        if (currentLine) lines.push(currentLine);
        return lines;
    };

    // Generar texto pre-formateado del pedido (mismo estilo que mostrador)
    const generateTicketTexto = () => {
        if (!ventaData) return '';
        const fecha = new Date();
        const fechaStr = fecha.toLocaleDateString('es-MX');
        const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const LINE = '================================';
        const MAX_CH = 32;
        const total = ventaData.productos.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);

        let texto = '';
        texto += `        PEDIDO #${ventaData.folio || 'Pendiente'}\n`;
        texto += `${LINE}\n`;
        texto += `Fecha: ${fechaStr}  Hora: ${horaStr}\n`;
        texto += `Cliente: ${ventaData.cliente_nombre || 'General'}\n`;
        texto += `Entrega: ${ventaData.tipo_entrega === 'domicilio' ? 'Domicilio' : 'Recoger en tienda'}\n`;
        if (ventaData.direccion_entrega) {
            const dirLines = wrapText(ventaData.direccion_entrega, MAX_CH - 5);
            texto += `Dir: ${dirLines[0]}\n`;
            for (let i = 1; i < dirLines.length; i++) {
                texto += `     ${dirLines[i]}\n`;
            }
        }
        if (ventaData.metodo_pago) {
            texto += `Pago: ${ventaData.metodo_pago === 'credito' ? '*** CREDITO ***' : ventaData.metodo_pago}\n`;
        }
        if (ventaData.estatus_pago !== 'pagado') {
            texto += `*** PENDIENTE DE PAGO ***\n`;
        }
        texto += `${LINE}\n`;
        texto += `CANT  DESCRIPCION          IMPORTE\n`;
        texto += `${LINE}\n`;

        ventaData.productos.forEach(item => {
            const subtotal = item.cantidad * item.precio_unitario;
            const cant = String(item.cantidad).padEnd(6);
            const nombre = (item.producto_nombre || '').substring(0, 20).padEnd(20);
            const imp = formatMoney(subtotal).padStart(8);
            texto += `${cant}${nombre}${imp}\n`;
            const um = item.unidad_medida || 'PZ';
            const pu = formatMoney(item.precio_unitario);
            texto += `      ${um} x ${pu}\n`;
        });

        texto += `${LINE}\n`;
        texto += `TOTAL:           ${formatMoney(total).padStart(16)}\n`;
        texto += `${LINE}\n`;
        if (ventaData.observaciones) {
            texto += `Obs: ${ventaData.observaciones}\n`;
            texto += `${LINE}\n`;
        }
        texto += `Atendio: ${ventaData.usuario_nombre || 'Sistema'}\n`;
        return texto;
    };

    // Generar HTML ticket (80mm) - mismas dimensiones que CounterSalesPage
    const generateTicketHTML = () => {
        if (!ventaData) return '';
        const ticketContent = generateTicketTexto();
        const folio = ventaData.folio || '';

        return `<!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Pedido - ${folio}</title>
                <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128+Text&display=swap" rel="stylesheet">
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    @page { size: 80mm auto; margin: 0; }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 12px;
                        font-weight: bold;
                        color: #000000;
                        margin: 0;
                        padding: 1mm 1mm 1mm 4mm;
                        white-space: pre-wrap;
                        background: white;
                        line-height: 1.4;
                    }
                    .barcode-container { text-align: center; margin: 5mm 0; white-space: normal; max-width: 36ch; }
                    .barcode { font-family: 'Libre Barcode 128 Text', cursive; font-size: 48px; color: #000000; line-height: 1; }
                    .barcode-text { font-family: 'Courier New', monospace; font-size: 10px; margin-top: 2mm; }
                    .ticket-logo { text-align: center; margin-bottom: 2mm; white-space: normal; max-width: 36ch; }
                    .ticket-logo img { display: block; margin: 0 auto; max-height: 100px; max-width: 250px; }
                </style>
            </head>
            <body>${empresaLogo ? `<div class="ticket-logo"><img src="${empresaLogo}" alt="Logo" /></div>` : ''}${ticketContent}
<div class="barcode-container">
<div class="barcode">${folio}</div>
<div class="barcode-text">${folio}</div>
</div>
            </body>
            </html>`;
    };

    // Generar HTML del pedido en tamaño carta
    const generateOrderHTML = () => {
        if (!ventaData) return '';

        const fecha = new Date().toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });

        const productosHTML = ventaData.productos.map((item) => `
            <tr>
                <td style="text-align: right;">${item.cantidad}</td>
                <td style="text-align: center;">${item.unidad_medida || 'PZ'}</td>
                <td>${item.producto_nombre}</td>
                <td style="text-align: right;">${formatMoney(item.precio_unitario)}</td>
                <td style="text-align: right;">${formatMoney(item.cantidad * item.precio_unitario)}</td>
            </tr>
        `).join('');

        const total = ventaData.productos.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Pedido ${ventaData.folio || ''}</title>
                <style>
                    @page {
                        size: letter;
                        margin: 0.8cm;
                    }
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        font-size: 11px;
                        line-height: 1.3;
                        color: #000;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 15px;
                        padding-bottom: 10px;
                        border-bottom: 1px solid #000;
                    }
                    .header-left {
                        display: flex;
                        align-items: center;
                    }
                    .header-logo {
                        max-width: 120px;
                        max-height: 70px;
                    }
                    .header-right {
                        text-align: right;
                        font-size: 11px;
                    }
                    .header-right .folio {
                        font-size: 14px;
                        font-weight: bold;
                        margin-bottom: 3px;
                    }
                    .header-right .fecha {
                        margin-bottom: 8px;
                    }
                    .header-right .cliente {
                        font-weight: bold;
                        font-size: 12px;
                    }
                    .header-right .direccion {
                        max-width: 300px;
                        font-size: 10px;
                        color: #333;
                    }
                    .header-right .info-line {
                        margin-bottom: 2px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 10px;
                    }
                    th {
                        background: #000;
                        color: white;
                        padding: 6px 4px;
                        text-align: left;
                        font-size: 10px;
                        text-transform: uppercase;
                    }
                    th:first-child { text-align: right; width: 70px; }
                    th:nth-child(2) { text-align: center; width: 35px; }
                    th:nth-child(4) { text-align: right; width: 65px; }
                    th:nth-child(5) { text-align: right; width: 75px; }
                    td {
                        padding: 5px 4px;
                        border-bottom: 1px solid #ddd;
                        font-size: 10px;
                    }
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 10px;
                    }
                    .totals-box {
                        text-align: right;
                    }
                    .totals-row {
                        font-size: 14px;
                        font-weight: bold;
                    }
                    .observaciones {
                        margin-top: 15px;
                        padding: 8px;
                        border: 1px solid #ddd;
                        font-size: 10px;
                    }
                    .observaciones strong {
                        text-transform: uppercase;
                    }
                    .firma-section {
                        margin-top: 40px;
                        text-align: center;
                    }
                    .firma-linea {
                        width: 200px;
                        border-bottom: 1px solid #000;
                        margin: 0 auto 5px auto;
                    }
                    .firma-texto {
                        font-size: 10px;
                    }
                    .footer {
                        margin-top: 20px;
                        text-align: center;
                        font-size: 9px;
                        color: #666;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="header-left">
                        ${empresaLogo ? `<img src="${empresaLogo}" alt="Logo" class="header-logo" />` : ''}
                    </div>
                    <div class="header-right">
                        <div class="folio">NOTA #${ventaData.folio || 'Pendiente'}</div>
                        <div class="fecha">FECHA: ${fecha}</div>
                        <div class="cliente">${ventaData.cliente_nombre || 'Cliente General'}</div>
                        ${ventaData.direccion_entrega ? `<div class="direccion">${ventaData.direccion_entrega}</div>` : ''}
                        <div class="info-line">${ventaData.tipo_entrega === 'domicilio' ? 'ENTREGA A DOMICILIO' : 'RECOGER EN TIENDA'}</div>
                        ${ventaData.metodo_pago ? `<div class="info-line">${ventaData.metodo_pago === 'credito' ? '*** CREDITO ***' : ventaData.metodo_pago.toUpperCase()}</div>` : ''}
                        ${ventaData.estatus_pago !== 'pagado' ? `<div class="info-line" style="color: red;">*** PENDIENTE DE PAGO ***</div>` : ''}
                    </div>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th style="text-align: right;">CANTIDAD</th>
                            <th style="text-align: center;">UM</th>
                            <th>DESCRIPCION DEL PRODUCTO</th>
                            <th style="text-align: right;">PRECIO</th>
                            <th style="text-align: right;">IMPORTE</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${productosHTML}
                    </tbody>
                </table>

                <div class="totals">
                    <div class="totals-box">
                        <div class="totals-row">
                            TOTAL DE LA VENTA ${formatMoney(total)}
                        </div>
                    </div>
                </div>

                ${ventaData.observaciones ? `
                <div class="observaciones">
                    <h3>Observaciones</h3>
                    <p>${ventaData.observaciones}</p>
                </div>
                ` : ''}

                ${ventaData.metodo_pago === 'credito' ? `
                <div class="firma-section">
                    <div class="firma-linea"></div>
                    <div class="firma-texto">Firma</div>
                </div>
                ` : ''}

                <div class="footer">
                    <p>Impreso el ${fecha} | Usuario: ${ventaData.usuario_nombre || 'Sistema'}</p>
                </div>
            </body>
            </html>
        `;
    };

    // Imprimir
    const handlePrint = async () => {
        if (!selectedPrinter) {
            showToast && showToast('Selecciona una impresora', 'warning');
            return;
        }

        setLoading(true);
        try {
            const esTicket = tipoImpresion === 'ticket';
            const htmlContent = esTicket ? generateTicketHTML() : generateOrderHTML();

            if (selectedPrinter === 'system' || !electronService.isElectronApp()) {
                const printWindow = window.open('', '_blank');
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
                printWindow.close();
                localStorage.setItem('pos_ultima_impresora', selectedPrinter);
                showToast && showToast('Pedido enviado a impresión', 'success');
            } else {
                const printOptions = esTicket
                    ? { pageSize: 'A4', landscape: false, margins: { marginType: 'none' } }
                    : { pageSize: 'Letter', landscape: false, margins: { marginType: 'default' } };

                const result = await electronService.printDocument(selectedPrinter, htmlContent, printOptions);

                if (result && result.success) {
                    localStorage.setItem('pos_ultima_impresora', selectedPrinter);
                    showToast && showToast('Pedido impreso correctamente', 'success');
                } else {
                    throw new Error(result?.message || 'Error al imprimir');
                }
            }

            onPrintComplete && onPrintComplete();
            onClose();
        } catch (error) {
            console.error('Error al imprimir:', error);
            showToast && showToast('Error al imprimir: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Atajos de teclado
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && !loading && selectedPrinter) {
                e.preventDefault();
                handlePrint();
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, loading, selectedPrinter]);

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Imprimir Pedido"
            size="small"
        >
            <div className="print-order-modal">
                {loadingPrinters ? (
                    <div className="loading-printers">
                        <Loader size={24} className="spinning" />
                        <span>Cargando impresoras...</span>
                    </div>
                ) : (
                    <>
                        {/* Selector de tipo de impresión */}
                        <div className="print-type-section">
                            <label className="section-label">
                                <FileText size={16} />
                                Tipo de Impresión
                            </label>
                            <div className="print-type-buttons">
                                <button
                                    className={`print-type-btn ${tipoImpresion === 'ticket' ? 'active' : ''}`}
                                    onClick={() => setTipoImpresion('ticket')}
                                >
                                    <Printer size={16} />
                                    Ticket (80mm)
                                </button>
                                <button
                                    className={`print-type-btn ${tipoImpresion === 'formato' ? 'active' : ''}`}
                                    onClick={() => setTipoImpresion('formato')}
                                >
                                    <FileText size={16} />
                                    Formato (Carta)
                                </button>
                            </div>
                        </div>

                        <div className="printer-section">
                            <label className="section-label">
                                <Printer size={16} />
                                Seleccionar Impresora
                            </label>
                            <div className="printer-list">
                                {printers.map((printer) => (
                                    <button
                                        key={printer.name}
                                        className={`printer-option ${selectedPrinter === printer.name ? 'selected' : ''}`}
                                        onClick={() => setSelectedPrinter(printer.name)}
                                    >
                                        <Printer size={18} />
                                        <span className="printer-name">
                                            {printer.displayName || printer.name}
                                        </span>
                                        {printer.isDefault && (
                                            <span className="default-badge">Por defecto</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="print-info">
                            <FileText size={16} />
                            <span>{tipoImpresion === 'ticket' ? 'Se imprimirá en rollo térmico (80mm)' : 'Se imprimirá en tamaño carta (Letter)'}</span>
                        </div>

                        <div className="print-actions">
                            <button className="btn-cancel" onClick={onClose} disabled={loading}>
                                Cancelar (Esc)
                            </button>
                            <button
                                className="btn-print"
                                onClick={handlePrint}
                                disabled={loading || !selectedPrinter}
                            >
                                {loading ? (
                                    <>
                                        <Loader size={18} className="spinning" />
                                        Imprimiendo...
                                    </>
                                ) : (
                                    <>
                                        <Printer size={18} />
                                        Imprimir (Enter)
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default PrintOrderModal;
